import type { DocumentFlow } from './Flow';

export interface Party {
  nif: string;
  name: string;
}

export type DocumentPartyKind = 'in_invoice' | 'out_invoice';

export const normalizeDocumentPartyKind = (kind?: string): DocumentPartyKind => (
  kind === 'out_invoice' || kind === 'customer' ? 'out_invoice' : 'in_invoice'
);

export interface TaxLine {
  base_amount: number;
  tax_rate: number;
  tax_amount: number;
  equivalence_surcharge_rate: number;
  equivalence_surcharge_amount: number;
}

export interface WithholdingLine {
  base_amount: number;
  personal_tax_withholding_rate: number;
  personal_tax_withholding_amount: number;
}

export interface Disbursement {
  description: string;
  disbursement_amount: number;
}

// Pedido an albaran line serves, like the albaranes of a factura line. The AI reads the
// number; consolidation sets the uuid.
export interface PedidoRef {
  uuid: string;
  numero: string;
}

// Albaranes carry no taxes: the line total is quantity × price × (1 − discount), the taxable base.
export interface AlbaranLine {
  product_code: string;
  concept: string;
  quantity: number;
  discount: number;
  total: number;
  unit_price_net: number;
  product_uuid?: string | null;
  product_link_source?: string | null;
  product_link_basis?: { supplierTaxId?: string; code?: string; concept?: string };
  pedido_numbers: PedidoRef[];
  // True when consolidation took the price from the pedido; false when it was read off the paper.
  valued_from_pedido: boolean;
}

// Un problema encontrado en el documento, sabiendo que fase lo encontro. El servidor lo manda asi
// para que la pantalla pueda colgar cada error de su etapa en vez de amontonarlos todos juntos.
export interface DocumentIssue {
  phase: string;
  message: string;
  type: string;
  // La capa del calculo que lo encontro, con el nombre que ya tiene el servidor. La pantalla agrupa
  // por ella en vez de adivinar la familia leyendo el texto. Vacia en los documentos ya guardados.
  layer?: string;
  // De que va el problema: el albaran, el producto o los dos. Se pinta delante y en negrita, con las
  // cifras debajo, para que la cifra que no cuadra no quede enterrada en la frase.
  subject?: string;
  // De que albaran salio. La pantalla cuelga de cada albaran lo suyo en vez de dar una lista larga
  // donde todo pesa igual. Vacio en lo que no sale de ninguno.
  albaran?: string;
  // Los datos de la fila, cada uno por su lado, y el rotulo de su columna, tal como los manda el
  // servidor. Con la frase entera en una celda la fila crecia a tres renglones y la cifra quedaba
  // enterrada. Vacios en los documentos guardados antes: esos caen a una sola columna.
  cell_list?: string[];
  column_list?: string[];
  // Las lineas del documento donde esta el problema, para que pulsarlo lleve a ellas. Vacia en los
  // guardados antes y en lo que no es de una linea.
  line_index_list?: number[];
}

export interface Albaran {
  // First-class domain identity ('' for a not-yet-persisted albarán). The Api
  // repo maps the entity-store row's uuid into/out of this field.
  uuid: string;
  number: string;
  date: string;
  issuer: Party;
  recipient: Party;
  lines: AlbaranLine[];
  state: string;
  pdf_url: string;
  pdf_path: string;
  invoice_uuid: string;
  total: number;
  parseRetryCount?: number;
  splitRetryCount?: number;
  isSplitRetryable?: boolean;
  error_message: string;
  notes: string;
  warning_message: string;
  issue_list: DocumentIssue[];
  priced: boolean;
  center: string;
  center_id?: string;
  kind?: DocumentPartyKind;
  third_id?: number | null;
  // D13: the flow it follows, as the server resolves it; never sent back.
  flow?: DocumentFlow | null;
}

// Albaran entity-type id — a readable slug (matches backend infra/constants.py).
export const USER_CENTER_ENTITY_TYPE_UUID = 'b290b8d5-6352-497d-b309-f5036bcc1edf';

// A highlighted field the editor hands to its header. The header draws whatever it is given: the
// kind says how, so a new field arrives as another builder here and the header stays untouched.
export type AlbaranFieldKind = 'option';

export interface AlbaranField {
  label: string;
  kind: AlbaranFieldKind;
  icon: string;
  value: string;
  optionValue: string;
  optionList: AlbaranFieldOption[];
  isEditable: boolean;
  onSelect: (optionValue: string) => void;
}

export interface AlbaranFieldOption {
  id: string;
  name: string;
}

export interface CenterFieldInput {
  centerName: string;
  centerId: string;
  centerList: AlbaranFieldOption[];
  isFormEditable: boolean;
  onSelect: (centerId: string) => void;
}

export const buildCenterField = (input: CenterFieldInput): AlbaranField | null => {
  if (!input.centerName && !input.centerList.length) return null;
  return {
    label: 'Centro',
    kind: 'option',
    icon: 'mdi-office-building-marker',
    value: input.centerName,
    optionValue: input.centerId,
    optionList: input.centerList,
    // La lista ya llega acotada a los centros repartidos a esa persona, asi que elegir no la saca de su sitio.
    isEditable: input.isFormEditable && input.centerList.length > 0,
    onSelect: input.onSelect,
  };
};

// Lifecycle phases. Working with pedidos adds a Consolidación phase after the
// reading, where the albaran is matched with its pedido before it is invoiced.
const ALBARAN_CONSOLIDABLE_STATE_LIST = ['validated', 'review_matching', 'error_matching'];
// Confirming with errors accepts them, like the factura.
const ALBARAN_CONFIRMABLE_STATE_LIST = ['review_matching', 'error_matching'];
export const ALBARAN_CONSOLIDATED_WITH_ERRORS_STATE = 'consolidated_with_errors';

export const albaranPhaseKeyList = (worksWithPedidos: boolean): string[] => (
  worksWithPedidos
    ? ['lectura', 'validated', 'matching', 'consolidated', 'invoiced']
    : ['lectura', 'validated', 'invoiced']
);

const ALBARAN_STATE_LABEL_MAP: Record<string, string> = {
  draft: 'Borrador',
  pending_split: 'Separando PDF',
  review_split: 'Revisar separación',
  pending: 'Procesando',
  review_parsing: 'Revisión de lectura',
  error_splitting: 'Error de separación',
  error_parsing: 'Error de lectura',
  insufficient_balance: 'Sin créditos',
  // D14: the company has no flow yet; it is read when it creates one.
  waiting_flow: 'Sin flujo',
  validated: 'Revisado',
  review_matching: 'Revisión con pedido',
  error_matching: 'Error con pedido',
  consolidated: 'Consolidado',
  [ALBARAN_CONSOLIDATED_WITH_ERRORS_STATE]: 'Consolidado con errores',
  invoiced: 'Facturado',
  duplicated: 'Duplicado',
};

export const albaranStateLabel = (state: string): string => ALBARAN_STATE_LABEL_MAP[state] || state;

// The same chip in the list and in the editor, so the state reads the same everywhere.
const ALBARAN_STATE_COLOR_MAP: Record<string, string> = {
  draft: 'grey',
  pending: 'warning',
  pending_split: 'info',
  review_split: 'warning',
  review_parsing: 'warning',
  error_parsing: 'error',
  error_splitting: 'error',
  insufficient_balance: 'warning',
  waiting_flow: 'warning',
  validated: 'success',
  review_matching: 'warning',
  error_matching: 'error',
  consolidated: 'success',
  [ALBARAN_CONSOLIDATED_WITH_ERRORS_STATE]: 'warning',
  invoiced: 'info',
  duplicated: 'deep-orange darken-1',
};

export const albaranStateColor = (state: string): string => ALBARAN_STATE_COLOR_MAP[state] || 'grey';

export const canConsolidateAlbaran =(state: string, worksWithPedidos: boolean): boolean => (
  worksWithPedidos && ALBARAN_CONSOLIDABLE_STATE_LIST.includes(state)
);

export const canConfirmAlbaranConsolidation = (state: string, worksWithPedidos: boolean): boolean => (
  worksWithPedidos && ALBARAN_CONFIRMABLE_STATE_LIST.includes(state)
);

// Unique uuids of the pedidos the lines are linked to, in encounter order.
// Linking one pedido number links it on every line that names it, as linkMatchingDeliveryNoteRefs does in the factura.
export const linkMatchingPedidoRefs = (lineList: AlbaranLine[], referenceNumber: string, link: PedidoRef): void => {
  if (!referenceNumber) return;
  lineList.forEach(line => (line.pedido_numbers || []).forEach(reference => {
    if (reference.numero !== referenceNumber) return;
    reference.numero = link.numero;
    reference.uuid = link.uuid;
  }));
};

export const albaranPedidoUuidList = (lineList: AlbaranLine[]): string[] => {
  const uuidList: string[] = [];
  lineList.forEach(line => (line.pedido_numbers || []).forEach(reference => {
    if (reference.uuid && !uuidList.includes(reference.uuid)) uuidList.push(reference.uuid);
  }));
  return uuidList;
};
